import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from '@tanstack/react-router';
import {
  FiArrowLeft,
  FiMusic,
  FiSearch,
  FiPlus,
  FiX,
  FiEdit2,
  FiTrash2,
  FiCheck,
  FiSave,
} from 'react-icons/fi';
import { Header } from '../../layout/Header';
import { BottomNav } from '../../layout/BottomNav';
import { useAuth } from '../../../hooks/useAuth';
import {
  useGetAllGenres,
  useCreateGenre,
  useUpdateGenre,
  useDeleteGenre,
  useUpdateUserGenres,
  type Genre,
} from '../../../api/settings';
import { useGetUserProfile } from '../../../api/profile';

import { extractApiError } from '../../../utils/apiError';
// ── Shared primitives ─────────────────────────────────────────────────────────

const Spinner = ({ size = 16 }: { size?: number }) => (
  <span
    style={{ width: size, height: size }}
    className="inline-block border-2 border-white/20 border-t-white rounded-full animate-spin"
  />
);

function Modal({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-zinc-900 border border-white/10 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
        {children}
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Validates that a genre name has no spaces. Returns an error string or null. */
function validateGenreName(name: string): string | null {
  if (!name.trim()) return 'Name cannot be empty.';
  if (/\s/.test(name)) return 'Genre names cannot contain spaces. Use _ or - instead.';
  return null;
}

function normalizeGenreName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[_-]{2,}/g, (m) => m[0]);
}

function getGenreSlugForApi(genre: Genre): string {
  const enriched = genre as Genre & {
    display_name?: string;
    genre_name?: string;
    title?: string;
    slug?: string;
  };
  const candidate =
    enriched.slug ??
    enriched.display_name ??
    enriched.genre_name ??
    enriched.title ??
    enriched.name;
  return String(candidate ?? '').trim();
}

// ── Main component ────────────────────────────────────────────────────────────

export const ModifyGenresPage: React.FC = () => {
  const { user } = useAuth();
  const { data: allGenres = [], isLoading: genresLoading } = useGetAllGenres();
  const { data: profile, isLoading: profileLoading } = useGetUserProfile(user?.id);

  const createGenreMut = useCreateGenre();
  const updateGenreMut = useUpdateGenre();
  const deleteGenreMut = useDeleteGenre();
  const saveGenresMut = useUpdateUserGenres();

  // ── State ──────────────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [initialIds, setInitialIds] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  const [newGenreError, setNewGenreError] = useState<string | null>(null);
  const [editDialog, setEditDialog] = useState<Genre | null>(null);
  const [editName, setEditName] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [deleteGenreId, setDeleteGenreId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingAutoSelectName, setPendingAutoSelectName] = useState<string | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const prevUserIdRef = useRef<string | number | null>(null);

  // ── Hydrate from user profile ──────────────────────────────────────────────
  useEffect(() => {
    if (!profile || !allGenres.length) return;
    if (isHydrated) return;

    // genres can arrive as strings, maps, or ints from the API
    const raw: unknown[] = profile.genres ?? [];
    const ids = new Set<number>();

    raw.forEach((g: unknown) => {
      if (typeof g === 'string') {
        const needle = g.toLowerCase();
        const found = allGenres.find(
          (ag) => ag.name.toLowerCase() === needle || (ag.slug?.toLowerCase() ?? '') === needle,
        );
        if (found) ids.add(found.id);
      } else if (typeof g === 'object' && g !== null && 'name' in g) {
        const name = (g as { name: string }).name;
        const needle = name.toLowerCase();
        const found = allGenres.find(
          (ag) => ag.name.toLowerCase() === needle || (ag.slug?.toLowerCase() ?? '') === needle,
        );
        if (found) ids.add(found.id);
      } else if (typeof g === 'number') {
        const found = allGenres.find((ag) => ag.id === g);
        if (found) ids.add(found.id);
      }
    });

    setSelectedIds(new Set(ids));
    setInitialIds(new Set(ids));
    setIsHydrated(true);
  }, [profile, allGenres, isHydrated]);

  useEffect(() => {
    const userId = user?.id ?? null;
    if (prevUserIdRef.current !== userId) {
      prevUserIdRef.current = userId;
      setIsHydrated(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!pendingAutoSelectName) return;
    const normalizedPending = normalizeGenreName(pendingAutoSelectName);
    const created = allGenres.find((genre) => normalizeGenreName(genre.name) === normalizedPending);
    if (!created) return;
    setSelectedIds((prev) => new Set([...prev, created.id]));
    setPendingAutoSelectName(null);
  }, [allGenres, pendingAutoSelectName]);

  const isLoading = genresLoading || profileLoading;

  const hasChanges = useMemo(() => {
    if (selectedIds.size !== initialIds.size) return true;
    for (const id of selectedIds) if (!initialIds.has(id)) return true;
    return false;
  }, [selectedIds, initialIds]);

  useEffect(() => {
    if (!hasChanges) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasChanges]);

  const filteredGenres = useMemo(
    () =>
      [...allGenres]
        .sort((a, b) => a.name.localeCompare(b.name))
        .filter((g) => g.name.toLowerCase().includes(search.toLowerCase())),
    [allGenres, search],
  );

  const selectedGenresList = useMemo(
    () => allGenres.filter((g) => selectedIds.has(g.id)),
    [allGenres, selectedIds],
  );

  /** User-created (non-global) genres owned by the current user */
  const myGenres = useMemo(
    () =>
      allGenres.filter(
        (g) => !g.is_global && g.creator !== null && String(g.creator) === String(user?.id),
      ),
    [allGenres, user?.id],
  );

  // ── Handlers ───────────────────────────────────────────────────────────────
  const toggleGenre = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const s = new Set(prev);
      if (s.has(id)) {
        s.delete(id);
      } else {
        s.add(id);
      }
      return s;
    });
  }, []);

  const handleAddGenre = async () => {
    if (createGenreMut.isPending) return;
    const name = search.trim();
    const err = validateGenreName(name);
    if (err) {
      setNewGenreError(err);
      return;
    }
    const normalizedName = normalizeGenreName(name);
    const duplicateExists = allGenres.some(
      (genre) => normalizeGenreName(genre.name) === normalizedName,
    );
    if (duplicateExists) {
      setNewGenreError('This genre already exists.');
      return;
    }
    setNewGenreError(null);
    setPendingAutoSelectName(name);
    try {
      const result = await createGenreMut.mutateAsync(name);
      setSearch('');
      if (result?.id) {
        setSelectedIds((prev) => new Set([...prev, result.id]));
        setPendingAutoSelectName(null);
      }
    } catch (error) {
      setPendingAutoSelectName(null);
      setNewGenreError(extractApiError(error, 'Could not create genre. Try again.'));
    }
  };

  const handleSave = async () => {
    if (!hasChanges) return;
    if (selectedIds.size === 0) return;
    if (!user?.id) return;
    setIsSaving(true);
    try {
      const names = allGenres
        .filter((g) => selectedIds.has(g.id))
        .map((g) => getGenreSlugForApi(g))
        .filter(Boolean);
      const uniqueGenreSlugs = Array.from(new Set(names));
      await saveGenresMut.mutateAsync({ userId: user.id, genreNames: uniqueGenreSlugs });
      setInitialIds(new Set(selectedIds));
    } finally {
      setIsSaving(false);
    }
  };

  const openEdit = (genre: Genre) => {
    setEditDialog(genre);
    setEditName(genre.name);
    setEditError(null);
  };

  const handleEditSave = async () => {
    if (!editDialog) return;
    if (updateGenreMut.isPending) return;
    const name = editName.trim();
    const err = validateGenreName(name);
    if (err) {
      setEditError(err);
      return;
    }
    const normalizedName = normalizeGenreName(name);
    const duplicateExists = allGenres.some(
      (genre) => genre.id !== editDialog.id && normalizeGenreName(genre.name) === normalizedName,
    );
    if (duplicateExists) {
      setEditError('A genre with this name already exists.');
      return;
    }
    try {
      await updateGenreMut.mutateAsync({ id: editDialog.id, name });
      setEditDialog(null);
    } catch (error) {
      setEditError(extractApiError(error, 'Could not update genre. Try again.'));
    }
  };

  const handleDelete = async () => {
    if (deleteGenreId === null) return;
    await deleteGenreMut.mutateAsync(deleteGenreId);
    setSelectedIds((prev) => {
      const s = new Set(prev);
      s.delete(deleteGenreId);
      return s;
    });
    setDeleteGenreId(null);
  };

  const canManage = (genre: Genre) =>
    !genre.is_global && genre.creator !== null && String(genre.creator) === String(user?.id);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <main className="pt-24 md:pt-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 mb-8 text-zinc-500 text-[12px] font-medium">
          <Link
            to="/profile/settings"
            className="flex items-center gap-1.5 hover:text-white transition-colors"
          >
            <FiArrowLeft size={13} className="text-blue-500/80" />
            <span>Settings</span>
          </Link>
          <span className="text-zinc-700">/</span>
          <span className="text-zinc-200">Modify Genres</span>
        </nav>

        {/* Page header */}
        <div className="flex items-start sm:items-center justify-between gap-4 mb-8 flex-col sm:flex-row">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-pink-500/10 border border-pink-500/20 flex items-center justify-center shrink-0">
              <FiMusic size={20} className="text-pink-400" />
            </div>
            <div>
              <h1 className="text-white font-bold text-[22px] leading-tight">Modify Genres</h1>
              <p className="text-zinc-500 text-[13px]">Customise your music genre preferences</p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={!hasChanges || isSaving || selectedIds.size === 0}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-[13px] font-semibold transition-all shrink-0"
          >
            {isSaving ? <Spinner size={14} /> : <FiSave size={14} />}
            Save Changes
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-60">
            <Spinner size={36} />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* ── Left panel: genre browser ── */}
            <div className="lg:col-span-3 space-y-4">
              {/* Search + Add */}
              <div className="bg-zinc-900/30 border border-white/5 rounded-2xl p-5">
                <h2 className="text-white font-semibold text-[14px] mb-4 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-md bg-pink-500/20 flex items-center justify-center">
                    <FiMusic size={10} className="text-pink-400" />
                  </span>
                  All Genres
                  <span className="ml-auto text-zinc-600 text-[11px] font-normal font-mono">
                    {allGenres.length} total
                  </span>
                </h2>

                {/* Search — shows "Add" button when nothing matches */}
                <div className="relative mb-3">
                  <FiSearch
                    size={13}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none"
                  />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setNewGenreError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && filteredGenres.length === 0 && search.trim()) {
                        handleAddGenre();
                      }
                    }}
                    placeholder="Search or create a genre (no spaces)…"
                    className="w-full bg-white/5 border border-white/8 rounded-xl pl-9 pr-8 py-2.5 text-[13px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-pink-500/40 transition-colors"
                  />
                  {search && (
                    <button
                      onClick={() => {
                        setSearch('');
                        setNewGenreError(null);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                    >
                      <FiX size={12} />
                    </button>
                  )}
                </div>
                {newGenreError && (
                  <p className="text-red-400 text-[11px] mb-2 ml-1">{newGenreError}</p>
                )}

                {/* Genres grid */}
                <div
                  className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 overflow-y-auto pr-1 mt-4"
                  style={{ maxHeight: 360 }}
                >
                  {filteredGenres.length === 0 ? (
                    <div className="col-span-full flex flex-col items-center gap-3 py-8">
                      <p className="text-zinc-600 text-[13px] text-center">
                        {search ? `No genres matching "${search}"` : 'No genres available yet.'}
                      </p>
                      {search.trim() && (
                        <button
                          onClick={handleAddGenre}
                          disabled={createGenreMut.isPending}
                          className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-pink-500/20 border border-pink-500/30 hover:bg-pink-500/30 disabled:opacity-40 disabled:cursor-not-allowed text-pink-300 text-[12px] font-semibold transition-all"
                        >
                          {createGenreMut.isPending ? <Spinner size={12} /> : <FiPlus size={12} />}
                          Add &ldquo;{search.trim()}&rdquo;
                        </button>
                      )}
                    </div>
                  ) : (
                    filteredGenres.map((genre) => {
                      const isSelected = selectedIds.has(genre.id);
                      const manageable = canManage(genre);
                      return (
                        <div
                          key={genre.id}
                          className={`group flex items-center gap-2 rounded-xl border px-2 py-1.5 transition-all ${
                            isSelected
                              ? 'bg-pink-500/15 border-pink-500/40'
                              : 'bg-white/4 border-white/10 hover:border-white/20'
                          }`}
                        >
                          <button
                            onClick={() => toggleGenre(genre.id)}
                            className="flex-1 min-w-0 inline-flex items-center gap-1.5 rounded-lg text-[12px] font-medium text-left transition-colors text-zinc-200 hover:text-white"
                          >
                            {isSelected && <FiCheck size={10} className="text-pink-400 shrink-0" />}
                            <span className="truncate">{genre.name}</span>
                          </button>
                          {manageable && (
                            <div className="flex items-center gap-0.5 shrink-0 opacity-70 group-hover:opacity-100">
                              <button
                                onClick={() => openEdit(genre)}
                                className="p-1 rounded-lg hover:bg-blue-500/15 text-zinc-600 hover:text-blue-400 focus-visible:text-blue-400 transition-colors"
                                title="Edit genre"
                              >
                                <FiEdit2 size={10} />
                              </button>
                              <button
                                onClick={() => setDeleteGenreId(genre.id)}
                                className="p-1 rounded-lg hover:bg-red-500/15 text-zinc-600 hover:text-red-400 focus-visible:text-red-400 transition-colors"
                                title="Delete genre"
                              >
                                <FiTrash2 size={10} />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* My custom genres panel (manage edit/delete) */}
              {myGenres.length > 0 && (
                <div className="bg-zinc-900/30 border border-white/5 rounded-2xl p-5">
                  <h2 className="text-white font-semibold text-[14px] mb-4 flex items-center gap-2">
                    <span className="w-5 h-5 rounded-md bg-violet-500/20 flex items-center justify-center">
                      <FiEdit2 size={10} className="text-violet-400" />
                    </span>
                    My Custom Genres
                    <span className="ml-auto text-zinc-600 text-[11px] font-normal font-mono">
                      {myGenres.length}
                    </span>
                  </h2>
                  <div className="space-y-2">
                    {myGenres.map((genre) => (
                      <div
                        key={genre.id}
                        className="flex items-center gap-3 px-4 py-3 bg-white/3 border border-white/[0.07] rounded-xl"
                      >
                        <span className="flex-1 text-white text-[13px]">{genre.name}</span>
                        <button
                          onClick={() => openEdit(genre)}
                          className="p-1.5 rounded-lg hover:bg-blue-500/15 text-zinc-500 hover:text-blue-400 transition-colors"
                          title="Edit"
                        >
                          <FiEdit2 size={13} />
                        </button>
                        <button
                          onClick={() => setDeleteGenreId(genre.id)}
                          className="p-1.5 rounded-lg hover:bg-red-500/15 text-zinc-500 hover:text-red-400 transition-colors"
                          title="Delete"
                        >
                          <FiTrash2 size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Right panel: selected genres ── */}
            <div className="lg:col-span-2">
              <div className="bg-zinc-900/30 border border-white/5 rounded-2xl p-5 lg:sticky lg:top-24">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-white font-semibold text-[14px] flex items-center gap-2">
                    <span className="w-5 h-5 rounded-md bg-blue-500/20 flex items-center justify-center">
                      <FiCheck size={10} className="text-blue-400" />
                    </span>
                    Your Genres
                  </h2>
                  <span className="text-zinc-500 text-[11px] font-mono">
                    {selectedGenresList.length} selected
                  </span>
                </div>

                {selectedGenresList.length === 0 ? (
                  <div className="text-center py-10">
                    <FiMusic size={28} className="mx-auto mb-3 text-zinc-700" />
                    <p className="text-zinc-600 text-[13px]">No genres selected yet.</p>
                    <p className="text-zinc-700 text-[11px] mt-1">
                      Click any genre on the left to add it.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {selectedGenresList.map((genre) => (
                        <span
                          key={genre.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] bg-pink-500/15 border border-pink-500/25 text-pink-300"
                        >
                          {genre.name}
                          <button
                            onClick={() => toggleGenre(genre.id)}
                            className="hover:text-red-400 transition-colors ml-0.5"
                            title="Remove"
                          >
                            <FiX size={11} />
                          </button>
                        </span>
                      ))}
                    </div>

                    {selectedIds.size === 0 && (
                      <p className="text-red-400 text-[11px] mb-3">
                        You must select at least one genre.
                      </p>
                    )}
                  </>
                )}

                {hasChanges && selectedIds.size === 0 && (
                  <div
                    className={selectedGenresList.length > 0 ? 'border-t border-white/5 pt-4' : ''}
                  >
                    <p className="text-red-400/80 text-[11px] text-center">
                      Select at least one genre to save.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      <BottomNav />

      {/* ── Edit Genre Modal ── */}
      {editDialog && (
        <Modal onClose={() => setEditDialog(null)}>
          <h3 className="text-white font-semibold text-[17px] mb-1">Edit Genre</h3>
          <p className="text-zinc-500 text-[12px] mb-4">Rename your custom genre</p>
          <input
            type="text"
            value={editName}
            onChange={(e) => {
              setEditName(e.target.value);
              setEditError(null);
            }}
            onKeyDown={(e) => e.key === 'Enter' && handleEditSave()}
            placeholder="Genre name (no spaces)"
            className={`w-full bg-white/5 border rounded-xl px-4 py-3 text-[14px] text-white focus:outline-none mb-1 transition-colors ${
              editError ? 'border-red-500/50' : 'border-white/10 focus:border-blue-500/40'
            }`}
            autoFocus
          />
          {editError && <p className="text-red-400 text-[11px] mb-3 ml-0.5">{editError}</p>}
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => setEditDialog(null)}
              className="flex-1 py-2.5 rounded-xl border border-white/10 text-zinc-400 text-[13px] hover:border-white/20 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleEditSave}
              disabled={!editName.trim() || updateGenreMut.isPending}
              className="flex-1 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white text-[13px] font-semibold transition-all"
            >
              {updateGenreMut.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Delete Genre Modal ── */}
      {deleteGenreId !== null && (
        <Modal onClose={() => setDeleteGenreId(null)}>
          <h3 className="text-white font-semibold text-[17px] mb-1">Delete Genre</h3>
          <p className="text-zinc-400 text-[13px] mb-6">
            Are you sure you want to delete{' '}
            <strong className="text-white">
              {allGenres.find((g) => g.id === deleteGenreId)?.name}
            </strong>
            ? This action cannot be undone.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setDeleteGenreId(null)}
              className="flex-1 py-2.5 rounded-xl border border-white/10 text-zinc-400 text-[13px] hover:border-white/20 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleteGenreMut.isPending}
              className="flex-1 py-2.5 rounded-xl bg-red-600/90 hover:bg-red-600 disabled:opacity-40 text-white text-[13px] font-semibold transition-all"
            >
              {deleteGenreMut.isPending ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};
